import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { activityService } from '../services/activityService';
import { displayCallOutcome, isTrackedCall } from '../utils/callOutcomeAnalytics';
import { feedCommentsService } from '../services/feedCommentsService';
import messageService from '../services/messageService';
import { supabase, isConfigured } from '../services/supabase';

const ACOLORS = ['#159c5f', '#22c55e', '#f59e0b', '#ab9bd6', '#fb7185', '#3186ba', '#f97316'];

function nameHash(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function Avatar({ name = '', size = 30 }) {
  const color = ACOLORS[nameHash(name) % ACOLORS.length];
  const initials = name.split(' ').slice(0, 2).map(x => x[0] || '').join('').toUpperCase() || '?';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `radial-gradient(circle at 32% 32%, ${color}52, ${color}18)`,
      color, border: `1.5px solid ${color}50`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.34, fontWeight: 700, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function relativeTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'gerade eben';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `vor ${days} Tag${days !== 1 ? 'en' : ''}`;
}

function isTermin(act) {
  return (act.type === 'call' && act.outcome === 'reached_appt') || act.type === 'meeting';
}

function isFeedActivity(act) {
  if (isTermin(act)) return true;
  if (isTrackedCall(act)) return true;
  return false;
}

function activityEmoji(act) {
  if (act.type === 'call' && act.outcome === 'reached_appt') return '🎉';
  if (act.type === 'meeting') return '📅';
  if (act.type === 'call') return '📞';
  return '📌';
}

function activityText(act, leadName) {
  const by = act.by || 'Jemand';
  const lead = leadName || act.leadName || (act.leadId ? `Lead #${act.leadId}` : 'einem Lead');
  if (act.type === 'call' && act.outcome === 'reached_appt') return `${by} hat Termin bei ${lead} gesetzt`;
  if (act.type === 'meeting') return `${by} hat Meeting mit ${lead} eingetragen`;
  if (act.type === 'call' && isTrackedCall(act)) {
    return `${by} hat ${lead} angerufen · ${displayCallOutcome(act)}`;
  }
  return `${by} hat Aktivität bei ${lead} geloggt`;
}

/* ── date helpers ── */
function isoDay(ts) {
  if (!ts) return null;
  return new Date(ts).toISOString().slice(0, 10);
}

function dayLabel(dateStr) {
  if (!dateStr) return '';
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const monday = (() => {
    const d = new Date(); const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
  })();
  if (dateStr === today) return 'Heute';
  if (dateStr === yesterday) return 'Gestern';
  if (dateStr >= monday) return 'Diese Woche';
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.`;
}

/* ── confetti ── */
function launchConfetti() {
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:99999';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  const pieces = Array.from({ length: 80 }, () => ({
    x: Math.random() * canvas.width,
    y: -10,
    vx: (Math.random() - 0.5) * 4,
    vy: Math.random() * 3 + 2,
    color: ['#159c5f', '#22c55e', '#f59e0b', '#ab9bd6', '#fb7185'][Math.floor(Math.random() * 5)],
    w: Math.random() * 8 + 4,
    h: Math.random() * 4 + 2,
    rot: Math.random() * 360,
    rotV: (Math.random() - 0.5) * 6,
  }));

  let frame;
  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    pieces.forEach(p => {
      p.x += p.vx; p.y += p.vy; p.rot += p.rotV; p.vy += 0.05;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot * Math.PI / 180);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    });
    if (pieces.some(p => p.y < canvas.height)) {
      frame = requestAnimationFrame(draw);
    } else {
      canvas.remove();
    }
  }
  draw();
  setTimeout(() => { cancelAnimationFrame(frame); canvas.remove(); }, 3000);
}

/* ── ding sound ── */
function playDing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch { /* ignore */ }
}

/* ── reactions localStorage helpers ── */
const REACTIONS_KEY = 'crm_feed_reactions';
const REACTION_EMOJIS = ['👍', '🔥', '🎉'];

function loadReactions() {
  try {
    const raw = localStorage.getItem(REACTIONS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveReactions(data) {
  try { localStorage.setItem(REACTIONS_KEY, JSON.stringify(data)); } catch { /* ignore */ }
}

/* ── Main component ── */
export default function TeamFeed({ currentUser, leads = [], maxItems = 30, onOpenLead, compact = false }) {
  const seenIds = useRef(new Set());
  const isInitialLoad = useRef(true);
  // track last-seen date per user for streak milestone detection
  const lastUserDateRef = useRef({});

  const [items, setItems] = useState(() => {
    const myId = currentUser?.id;
    const flat = activityService.getAllFlat();
    return flat
      .filter(a => {
        if (!a.ts && !a.date) return false;
        if (myId && a.byUserId && String(a.byUserId) === String(myId)) return false;
        return isFeedActivity(a);
      })
      .sort((a, b) => {
        const ta = a.ts ? new Date(a.ts).getTime() : 0;
        const tb = b.ts ? new Date(b.ts).getTime() : 0;
        return tb - ta;
      })
      .slice(0, maxItems)
      .map(a => ({ ...a, _feedId: `${a.id}_${a.leadId}` }));
  });

  const leadMap = useRef(new Map());
  useEffect(() => {
    leadMap.current = new Map((leads || []).map(l => [String(l.id), l]));
  }, [leads]);

  // Seed seenIds from initial items
  useEffect(() => {
    items.forEach(a => seenIds.current.add(a._feedId));
    // Seed last-seen dates per user from initial items
    items.forEach(a => {
      if (a.byUserId && a.ts) {
        const d = isoDay(a.ts);
        if (!lastUserDateRef.current[a.byUserId]) lastUserDateRef.current[a.byUserId] = d;
      }
    });
    isInitialLoad.current = false;
  }, []); // eslint-disable-line

  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('crm_feed_sound') === '1');
  const [reactions, setReactions] = useState(loadReactions);
  const [replyOpen, setReplyOpen] = useState(null); // actId
  const [replyText, setReplyText] = useState('');
  const [replySent, setReplySent] = useState(null); // actId
  const [hoveredId, setHoveredId] = useState(null);
  const [feedComments, setFeedComments] = useState(() => feedCommentsService.getAll());
  const [commentOpen, setCommentOpen] = useState(null);
  const [commentText, setCommentText] = useState('');

  const todayLeaderboard = useMemo(() => {
    const todayStrLb = new Date().toISOString().slice(0, 10);
    const counts = {};
    activityService.getAllFlat().forEach(a => {
      if (!isTermin(a) || isoDay(a.ts) !== todayStrLb) return;
      const key = a.byUserId || a.by || '?';
      const name = a.by || 'Unbekannt';
      if (!counts[key]) counts[key] = { name, count: 0, userId: a.byUserId };
      counts[key].count += 1;
    });
    return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [items]);

  const todayCallDigest = useMemo(() => {
    const todayStrLb = new Date().toISOString().slice(0, 10);
    const counts = {};
    activityService.getAllFlat().forEach(a => {
      if (!isTrackedCall(a) || isoDay(a.ts) !== todayStrLb) return;
      const key = a.byUserId || a.by || '?';
      const name = a.by || 'Unbekannt';
      if (!counts[key]) counts[key] = { name, count: 0, userId: a.byUserId };
      counts[key].count += 1;
    });
    return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [items]);

  // Persist sound preference
  useEffect(() => {
    localStorage.setItem('crm_feed_sound', soundOn ? '1' : '0');
  }, [soundOn]);

  // Reset feed at midnight
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
    const ms = midnight.getTime() - now.getTime();
    const timer = setTimeout(() => {
      setItems([]);
      seenIds.current = new Set();
    }, ms);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    function onComments() { setFeedComments(feedCommentsService.getAll()); }
    window.addEventListener('crm-feed-comments-changed', onComments);
    return () => window.removeEventListener('crm-feed-comments-changed', onComments);
  }, []);

  useEffect(() => {
    if (!isConfigured) return undefined;
    let cancelled = false;
    feedCommentsService.pullRecent(14)
      .then(grouped => { if (!cancelled) setFeedComments(grouped); })
      .catch(() => { /* offline / RLS — Cache bleibt */ });
    const unsub = feedCommentsService.subscribeRealtime();
    return () => { cancelled = true; unsub(); };
  }, []);

  const postComment = useCallback(async (act) => {
    if (!commentText.trim() || !currentUser?.id || !act?._feedId || !act?.leadId) return;
    try {
      await feedCommentsService.add(act._feedId, {
        userId: currentUser.id,
        userName: currentUser.name || currentUser.email || 'Nutzer',
        text: commentText.trim(),
        leadId: act.leadId,
      });
      setCommentText('');
      setCommentOpen(null);
      setFeedComments(feedCommentsService.getAll());
    } catch {
      try {
        window.dispatchEvent(new CustomEvent('crm-error', {
          detail: { code: 'FEED_COMMENT_FAIL', message: 'Kommentar konnte nicht gespeichert werden.' },
        }));
      } catch { /* ignore */ }
    }
  }, [commentText, currentUser]);

  // Supabase reactions subscription
  useEffect(() => {
    if (!isConfigured) return;
    let channel;
    try {
      channel = supabase
        .channel('feed_reactions')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_feed_reactions' }, payload => {
          const row = payload.new;
          if (!row?.activity_id) return;
          setReactions(prev => {
            const next = { ...prev };
            const aId = row.activity_id;
            if (!next[aId]) next[aId] = { '👍': [], '🔥': [], '🎉': [] };
            const emojiMap = { ...next[aId] };
            REACTION_EMOJIS.forEach(em => {
              if (!Array.isArray(emojiMap[em])) emojiMap[em] = [];
            });
            if (row.emoji && row.user_id) {
              // This is an upsert — just ensure user is in the list
              if (!emojiMap[row.emoji].includes(row.user_id)) {
                emojiMap[row.emoji] = [...emojiMap[row.emoji], row.user_id];
              }
            }
            next[aId] = emojiMap;
            saveReactions(next);
            return next;
          });
        })
        .subscribe();
    } catch { /* ignore */ }
    return () => { if (channel) { try { supabase.removeChannel(channel); } catch { /* ignore */ } } };
  }, []);

  // crm-activities-changed handler
  useEffect(() => {
    const myId = currentUser?.id;

    function handler() {
      const flat = activityService.getAllFlat();
      const newItems = [];
      const milestoneItems = [];

      for (const a of flat) {
        if (!a.ts) continue;
        if (myId && a.byUserId && String(a.byUserId) === String(myId)) continue;
        if (!isFeedActivity(a)) continue;
        const fid = `${a.id}_${a.leadId}`;
        if (seenIds.current.has(fid)) continue;
        const ts = new Date(a.ts).getTime();
        if (ts < Date.now() - 7 * 24 * 60 * 60 * 1000) continue;
        seenIds.current.add(fid);
        newItems.push({ ...a, _feedId: fid });

        // Confetti + sound for new appointments
        if (!isInitialLoad.current && a.type === 'call' && a.outcome === 'reached_appt') {
          launchConfetti();
          if (soundOn) playDing();
        }

        // Streak milestone detection
        if (!isInitialLoad.current && a.byUserId && a.ts) {
          const actDay = isoDay(a.ts);
          const lastDay = lastUserDateRef.current[a.byUserId];
          if (lastDay && lastDay !== actDay) {
            // First activity of the day from this user
            const name = (a.by || '').split(' ')[0] || 'Jemand';
            milestoneItems.push({
              _feedId: `streak_${a.byUserId}_${actDay}`,
              _isMilestone: true,
              by: a.by,
              byUserId: a.byUserId,
              ts: a.ts,
              _milestoneText: `🔥 ${name} ist auf einem Streak!`,
            });
          }
          lastUserDateRef.current[a.byUserId] = actDay;
        }
      }

      if (newItems.length > 0 || milestoneItems.length > 0) {
        const all = [...milestoneItems, ...newItems];
        setItems(prev => [...all, ...prev].slice(0, maxItems));
      }
    }

    window.addEventListener('crm-activities-changed', handler);
    return () => window.removeEventListener('crm-activities-changed', handler);
  }, [currentUser?.id, maxItems, soundOn]);

  /* ── reaction toggle ── */
  const toggleReaction = useCallback(async (actId, emoji) => {
    const uid = currentUser?.id;
    if (!uid) return;
    setReactions(prev => {
      const next = { ...prev };
      if (!next[actId]) next[actId] = { '👍': [], '🔥': [], '🎉': [] };
      const em = { ...next[actId] };
      REACTION_EMOJIS.forEach(e => { if (!Array.isArray(em[e])) em[e] = []; });
      const hadIt = em[emoji].includes(uid);
      em[emoji] = hadIt ? em[emoji].filter(x => x !== uid) : [...em[emoji], uid];
      next[actId] = em;
      saveReactions(next);
      return next;
    });

    if (isConfigured) {
      try {
        const uid2 = String(uid);
        const { data: existing } = await supabase
          .from('crm_feed_reactions')
          .select('id')
          .eq('activity_id', actId)
          .eq('emoji', emoji)
          .eq('user_id', uid2)
          .maybeSingle();

        const hadIt = reactions[actId]?.[emoji]?.includes(uid2);
        if (!hadIt) {
          // Adding
          const actRow = items.find(x => x._feedId === actId);
          await supabase.from('crm_feed_reactions').upsert({
            activity_id: actId,
            emoji,
            user_id: uid2,
            lead_id: actRow?.leadId ? String(actRow.leadId) : null,
          }, { onConflict: 'activity_id,emoji,user_id' });
        } else {
          // Removing
          if (existing?.id) {
            await supabase.from('crm_feed_reactions').delete().eq('id', existing.id);
          }
        }
      } catch { /* ignore */ }
    }
  }, [currentUser?.id, reactions]);

  /* ── reply send ── */
  const handleReplySend = useCallback(async (act) => {
    if (!replyText.trim() || !currentUser?.id) return;
    const toId = act.byUserId;
    if (!toId || String(toId) === String(currentUser.id)) {
      setReplyOpen(null);
      setReplyText('');
      return;
    }
    const lead = act.leadId ? leadMap.current.get(String(act.leadId)) : null;
    const leadName = lead ? (lead.name || lead.company) : (act.leadName || null);
    const text = activityText(act, leadName);
    try {
      await messageService.send(toId, {
        from: currentUser.id,
        fromName: currentUser.name || currentUser.email || 'Jemand',
        type: 'general',
        subject: `Re: ${text.slice(0, 60)}`,
        body: replyText.trim(),
      });
      setReplySent(act._feedId);
      setTimeout(() => setReplySent(null), 2000);
    } catch { /* ignore */ }
    setReplyOpen(null);
    setReplyText('');
  }, [replyText, currentUser, leadMap]);

  /* ── today stats ── */
  const todayStr = new Date().toISOString().slice(0, 10);

  const filtered = items.filter(a => isoDay(a.ts) === todayStr);
  const todayAppts = items.filter(a => !a._isMilestone && isoDay(a.ts) === todayStr && a.type === 'call' && a.outcome === 'reached_appt').length;
  const todayMeetings = items.filter(a => !a._isMilestone && isoDay(a.ts) === todayStr && a.type === 'meeting').length;
  const todayTotal = todayAppts + todayMeetings;

  /* ── group by date ── */
  const groups = [];
  let lastGroup = null;
  filtered.forEach(act => {
    const day = act._isMilestone ? isoDay(act.ts) : isoDay(act.ts);
    const label = dayLabel(day);
    if (!lastGroup || lastGroup.label !== label) {
      lastGroup = { label, items: [] };
      groups.push(lastGroup);
    }
    lastGroup.items.push(act);
  });

  const maxHeight = compact ? 'calc(100vh - 140px)' : 360;

  if (filtered.length === 0) {
    return (
      <div className="gl-card" style={{ borderRadius: 16, padding: '28px 20px', textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>Noch keine Aktivitäten heute</div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>Anrufe und Termine erscheinen hier live · Reset um 0:00 Uhr</div>
      </div>
    );
  }

  return (
    <div className="gl-card" style={{ borderRadius: 16, overflow: 'hidden' }}>
      {/* ── Header ── */}
      <div style={{ padding: compact ? '10px 14px 10px' : '14px 18px 14px', borderBottom: '1px solid rgba(255,255,255,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>Team-Feed · Heute</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: '#22c55e', background: 'rgba(34,197,94,0.14)', border: '1px solid rgba(34,197,94,0.28)', borderRadius: 8, padding: '1px 6px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Live</span>
          {todayTotal > 0 && (
            <span style={{ marginLeft: 4, fontSize: 13, fontWeight: 800, color: '#22c55e' }}>{todayTotal} Termin{todayTotal !== 1 ? 'e' : ''}</span>
          )}
          <button
            onClick={() => setSoundOn(v => !v)}
            title={soundOn ? 'Sound aus' : 'Sound an'}
            style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 7, background: soundOn ? 'rgba(29,190,116,0.12)' : 'rgba(255,255,255,0.3)', border: `1px solid ${soundOn ? 'rgba(29,190,116,0.3)' : 'rgba(255,255,255,0.5)'}`, color: soundOn ? 'var(--accent)' : 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, backdropFilter: 'blur(8px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {soundOn
                ? <><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></>
                : <><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></>}
            </svg>
          </button>
        </div>
      </div>

      {/* ── Feed items — today only ── */}
      <div style={{ maxHeight, overflowY: 'auto' }}>
        {filtered.map((act) => {
          if (act._isMilestone) {
            return (
              <div key={act._feedId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 14px' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 20, padding: '2px 12px', backdropFilter: 'blur(6px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5)' }}>
                  {act._milestoneText}
                </span>
              </div>
            );
          }

          const lead = act.leadId ? leadMap.current.get(String(act.leadId)) : null;
          const leadName = lead ? (lead.name || lead.company) : (act.leadName || null);
          const isAppt = act.type === 'call' && act.outcome === 'reached_appt';
          const emoji = activityEmoji(act);
          const text = activityText(act, leadName);
          const timeStr = relativeTime(act.ts);
          const isHovered = hoveredId === act._feedId;
          const actReactions = reactions[act._feedId] || {};
          const hasAnyReaction = REACTION_EMOJIS.some(e => (actReactions[e] || []).length > 0);
          const uid = String(currentUser?.id || '');
          const isReplyOpen = replyOpen === act._feedId;
          const wasSent = replySent === act._feedId;
          const actComments = feedComments[act._feedId] || [];
          const isCommentOpen = commentOpen === act._feedId;

          return (
            <div
              key={act._feedId}
              onMouseEnter={() => setHoveredId(act._feedId)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                borderBottom: '1px solid rgba(255,255,255,0.28)',
                background: isHovered
                  ? (isAppt ? 'rgba(34,197,94,0.10)' : 'rgba(255,255,255,0.28)')
                  : (isAppt ? 'rgba(34,197,94,0.04)' : 'transparent'),
                transition: 'background .16s, padding .16s',
                borderLeft: isAppt ? '3px solid rgba(34,197,94,0.5)' : '3px solid transparent',
              }}
            >
              <div
                onClick={() => { if (lead && onOpenLead) onOpenLead(lead); }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: isHovered ? '12px 16px' : '9px 16px',
                  cursor: lead && onOpenLead ? 'pointer' : 'default',
                  transition: 'padding .16s',
                }}
              >
                <Avatar name={act.by || '?'} size={isHovered ? 32 : 28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: isHovered ? 13 : 12,
                    color: 'var(--text)',
                    lineHeight: 1.45,
                    fontWeight: isAppt ? 600 : 400,
                    transition: 'font-size .16s',
                  }}>
                    {text}
                  </div>
                  {act.note && (
                    <div style={{
                      fontSize: 11, color: 'var(--text2)', marginTop: isHovered ? 5 : 2,
                      fontStyle: 'italic',
                      overflow: isHovered ? 'visible' : 'hidden',
                      textOverflow: isHovered ? 'unset' : 'ellipsis',
                      whiteSpace: isHovered ? 'normal' : 'nowrap',
                      lineHeight: 1.5, transition: 'margin .16s',
                    }}>
                      "{act.note}"
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  <span style={{ fontSize: 13 }}>{emoji}</span>
                  <div style={{ fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{timeStr}</div>
                </div>
              </div>

              {/* Actions + reactions on hover */}
              {(isHovered || hasAnyReaction) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 16px 8px 58px', flexWrap: 'wrap' }}>
                  {REACTION_EMOJIS.map(em => {
                    const users = actReactions[em] || [];
                    const mine = users.includes(uid);
                    return (
                      <button key={em} onClick={e => { e.stopPropagation(); toggleReaction(act._feedId, em); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 10, background: mine ? 'var(--accent-bg)' : 'rgba(255,255,255,0.4)', border: `1px solid ${mine ? 'var(--accent-border)' : 'rgba(255,255,255,0.6)'}`, cursor: 'pointer', fontSize: 11, backdropFilter: 'blur(8px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)', transition: 'all .1s' }}>
                        <span>{em}</span>
                        {users.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: mine ? 'var(--accent)' : 'var(--text3)' }}>{users.length}</span>}
                      </button>
                    );
                  })}
                  {(actComments.length > 0 || isHovered) && (
                    <button onClick={e => { e.stopPropagation(); setCommentOpen(isCommentOpen ? null : act._feedId); setCommentText(''); }}
                      style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', background: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.55)', borderRadius: 10, padding: '2px 8px', cursor: 'pointer', backdropFilter: 'blur(6px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>
                      💬{actComments.length > 0 ? ` ${actComments.length}` : ''}
                    </button>
                  )}
                  {isHovered && act.byUserId && String(act.byUserId) !== uid && (
                    <button onClick={e => { e.stopPropagation(); setReplyOpen(isReplyOpen ? null : act._feedId); setReplyText(''); }}
                      style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 10, padding: '2px 8px', cursor: 'pointer', backdropFilter: 'blur(6px)' }}>
                      {wasSent ? '✓' : '↩ Antworten'}
                    </button>
                  )}
                </div>
              )}

              {/* Comments */}
              {actComments.length > 0 && (
                <div style={{ padding: '0 16px 6px 58px' }}>
                  {actComments.map(c => (
                    <div key={c.id} style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 3, lineHeight: 1.4 }}>
                      <strong style={{ color: 'var(--text)' }}>{c.userName}:</strong> {c.text}
                      <span style={{ color: 'var(--text3)', marginLeft: 5, fontSize: 10 }}>{relativeTime(c.ts)}</span>
                    </div>
                  ))}
                </div>
              )}
              {isCommentOpen && (
                <div style={{ padding: '4px 14px 10px 58px', display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                  <input autoFocus value={commentText} onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') postComment(act); if (e.key === 'Escape') { setCommentOpen(null); setCommentText(''); } }}
                    placeholder="Team-Kommentar…"
                    className="gl-input"
                    style={{ flex: 1, height: 30, color: 'var(--text)', fontSize: 12, padding: '0 10px', outline: 'none', fontFamily: 'var(--font)' }} />
                  <button onClick={() => postComment(act)} className="gl-btn-primary" style={{ height: 30, padding: '0 10px', borderRadius: 7, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Posten</button>
                </div>
              )}
              {isReplyOpen && (
                <div style={{ padding: '4px 14px 10px', display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                  <input autoFocus value={replyText} onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleReplySend(act); if (e.key === 'Escape') { setReplyOpen(null); setReplyText(''); } }}
                    placeholder="Antwort eingeben…"
                    className="gl-input"
                    style={{ flex: 1, height: 30, color: 'var(--text)', fontSize: 12, padding: '0 10px', outline: 'none', fontFamily: 'var(--font)' }} />
                  <button onClick={() => handleReplySend(act)} className="gl-btn-primary" style={{ height: 30, padding: '0 10px', borderRadius: 7, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Senden</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
